import type { AxisConfiguration } from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import type {
    KpiAxisConfiguration,
    KpiDataRange,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import type { AxisGroupByControlsMeta } from 'components/molecules/PackArchitecture/axisGroupByControlsMeta.types';
import type { KpiAxisControlsMeta } from 'components/molecules/PackArchitecture/kpiAxisControlsMeta.types';
import { createDefaultKpiAxisConfiguration } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import {
    buildDefaultDimensionAxisUserInputs,
    buildDimensionAxisUserInputs,
    buildKpiAxisUserInputs,
    serializePpaChartUserInputs,
} from 'components/pages/PackArchitecture/packArchitectureChartRequest.helpers';
import {
    getAxisConfigurationDefaultsFromMeta,
    getKpiAxisConfigurationDefaultsFromMeta,
} from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import { DEFAULT_WHITESPACE_PAGE_LIMIT } from 'contexts/whitespaceAnalysisContext.helpers';
import type { UserInput } from 'hooks/ppa/useFetchPricePackArchitecture';

export type WhitespaceUserInput = {
    input_id: string;
    input_val: string | number | boolean;
};

const WHITESPACE_EXCLUDED_USER_INPUT_IDS = new Set([
    'consolidate_as_others',
    'group_rest_as_other',
    'own_dimension_sort',
]);

// Namespaced Y-axis attribute-bucket (e.g. Pack_Range) sort wire ids - kept
// distinct from the X-axis's own `sort_by`/mode ids so both axes can carry
// independent dimension configs at once. Mirrors
// WS_BUCKET_SORT_BY_INPUT_ID / WS_BUCKET_SORT_DIRECTION_ID_PREFIX in
// app/services/ppa/helpers/ppa_constants.py.
const WHITESPACE_BUCKET_SORT_BY_INPUT_ID = 'bucket_sort_by';
const WHITESPACE_BUCKET_SORT_DIRECTION_ID_PREFIX = 'bucket_';
const WHITESPACE_BUCKET_SORT_MODE_IDS = [
    'sort_on_pack_size',
    'sort_on_value_sales',
    'sort_on_volume_sales',
] as const;
// Namespaced Y-axis attribute-bucket Top N wire id - kept distinct from the
// X-axis's own `show_top_n` so both axes can be capped independently at
// once. Mirrors WS_BUCKET_SHOW_TOP_INPUT_ID in
// app/services/ppa/helpers/ppa_constants.py.
const WHITESPACE_BUCKET_SHOW_TOP_INPUT_ID = 'bucket_show_top_n';
const WHITESPACE_BUCKET_AXIS_USER_INPUT_IDS = new Set([
    WHITESPACE_BUCKET_SORT_BY_INPUT_ID,
    WHITESPACE_BUCKET_SHOW_TOP_INPUT_ID,
    ...WHITESPACE_BUCKET_SORT_MODE_IDS.map((mode) => `${WHITESPACE_BUCKET_SORT_DIRECTION_ID_PREFIX}${mode}`),
]);

/** Y-axis slider bounds are omitted until the user applies KPI axis config (not on initial load). */
const WHITESPACE_DEFAULT_Y_AXIS_EXCLUDED_INPUT_IDS = new Set(['min', 'max']);

const WHITESPACE_KPI_AXIS_PREVIEW_INPUT_IDS = new Set([
    'min',
    'max',
    'bin_method',
    'count',
    'break_point',
]);

/** Only strips X-axis dimension inputs; Y-axis KPI inputs (min/max/bin_method/count) are intentionally left in so they are preserved in preview requests. */
const AXIS_PREVIEW_USER_INPUT_IDS = new Set([
    'sort_by',
    'sort_order',
    'sort_on_alphabet',
    'sort_on_value_sales',
    'sort_on_volume_sales',
    'show_top_n',
    ...WHITESPACE_EXCLUDED_USER_INPUT_IDS,
]);

export const toWhitespaceUserInputs = (inputs: UserInput[] = []): WhitespaceUserInput[] =>
    inputs.map(({ id, input_val }) => ({
        input_id: id,
        input_val,
    }));

/** Reads a positive numeric Show-Top-N value (e.g. `show_top_n` / `bucket_show_top_n`) out of a `user_inputs` array. */
export const extractWhitespaceShowTopUserInput = (
    userInputs: WhitespaceUserInput[] | undefined,
    inputId: string,
): number | undefined => {
    const entry = (userInputs ?? []).find((input) => input.input_id === inputId);
    if (!entry) return undefined;
    const parsed = Number(entry.input_val);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

export const buildWhitespaceXAxisUserInputs = (
    dimensionConfig?: AxisConfiguration,
    groupByControlsMeta?: AxisGroupByControlsMeta | null,
) => {
    if (dimensionConfig) {
        return buildDimensionAxisUserInputs({
            ...dimensionConfig,
            groupRestAsOther: false,
            showOwnVendorFirst: false,
        });
    }
    const defaults = buildDefaultDimensionAxisUserInputs(groupByControlsMeta);
    if (!defaults) return undefined;
    return {
        ...defaults,
        group_rest_as_other: false,
    };
};

export const buildWhitespaceYAxisKpiConfig = (
    kpiConfig?: KpiAxisConfiguration,
    kpiControlsMeta?: KpiAxisControlsMeta | null,
    kpiDataRange?: KpiDataRange,
): KpiAxisConfiguration | undefined => {
    if (kpiConfig) {
        return kpiConfig;
    }
    if (!kpiControlsMeta || !kpiDataRange) {
        return undefined;
    }
    const metaDefaults = getKpiAxisConfigurationDefaultsFromMeta(kpiControlsMeta);
    return createDefaultKpiAxisConfiguration(kpiDataRange, metaDefaults);
};

/** Whitespace Y-axis uses the same `user_inputs` contract as Pack Architecture KPI axis config. */
export const serializeWhitespaceYAxisUserInputs = (config: KpiAxisConfiguration): WhitespaceUserInput[] =>
    toWhitespaceUserInputs(serializePpaChartUserInputs(buildKpiAxisUserInputs(config)) ?? []);

export const serializeWhitespaceYAxisUserInputsFromMeta = (
    kpiControlsMeta: KpiAxisControlsMeta,
    kpiDataRange?: KpiDataRange,
): WhitespaceUserInput[] => {
    const config = buildWhitespaceYAxisKpiConfig(undefined, kpiControlsMeta, kpiDataRange);
    return config ? serializeWhitespaceYAxisUserInputs(config) : [];
};

/**
 * Y-axis attribute-bucket (e.g. Pack_Range) sort + Top N settings, namespaced
 * to `bucket_sort_by` / `bucket_<mode>` / `bucket_show_top_n` on the wire so
 * they never collide with the X-axis's own `sort_by`/mode/`show_top_n` ids.
 * There is deliberately no group-rest-as-other entry here - buckets beyond
 * the Top N cap are simply dropped, never rolled up.
 */
export const buildWhitespaceYAxisGroupByUserInputs = (
    dimensionConfig?: AxisConfiguration,
    groupByControlsMeta?: AxisGroupByControlsMeta | null,
): WhitespaceUserInput[] => {
    const sortBy = dimensionConfig?.sortBy ?? getAxisConfigurationDefaultsFromMeta(groupByControlsMeta)?.sortBy;
    if (!sortBy) return [];
    const sortOrder =
        dimensionConfig?.sortOrder ?? getAxisConfigurationDefaultsFromMeta(groupByControlsMeta)?.sortOrder ?? 'desc';

    const inputs: WhitespaceUserInput[] = [
        { input_id: WHITESPACE_BUCKET_SORT_BY_INPUT_ID, input_val: sortBy },
        { input_id: `${WHITESPACE_BUCKET_SORT_DIRECTION_ID_PREFIX}${sortBy}`, input_val: sortOrder },
    ];
    if (typeof dimensionConfig?.showTop === 'number' && Number.isFinite(dimensionConfig.showTop) && dimensionConfig.showTop > 0) {
        inputs.push({ input_id: WHITESPACE_BUCKET_SHOW_TOP_INPUT_ID, input_val: dimensionConfig.showTop });
    }
    return inputs;
};

export const mergeWhitespaceUserInputs = ({
    xAxisDimensionConfig,
    yAxisKpiConfig,
    yAxisDimensionConfig,
    xAxisGroupByControlsMeta,
    yAxisKpiControlsMeta,
    yAxisGroupByControlsMeta,
    yAxisKpiDataRange,
    isYAxisAttribute = false,
}: {
    xAxisDimensionConfig?: AxisConfiguration;
    yAxisKpiConfig?: KpiAxisConfiguration;
    yAxisDimensionConfig?: AxisConfiguration;
    xAxisGroupByControlsMeta?: AxisGroupByControlsMeta | null;
    yAxisKpiControlsMeta?: KpiAxisControlsMeta | null;
    yAxisGroupByControlsMeta?: AxisGroupByControlsMeta | null;
    yAxisKpiDataRange?: KpiDataRange;
    isYAxisAttribute?: boolean;
}): WhitespaceUserInput[] => {
    const xInputs = serializePpaChartUserInputs(
        buildWhitespaceXAxisUserInputs(xAxisDimensionConfig, xAxisGroupByControlsMeta),
    );

    let yInputs: WhitespaceUserInput[];
    if (isYAxisAttribute) {
        yInputs = buildWhitespaceYAxisGroupByUserInputs(yAxisDimensionConfig, yAxisGroupByControlsMeta);
    } else {
        const yConfig =
            yAxisKpiConfig ?? buildWhitespaceYAxisKpiConfig(undefined, yAxisKpiControlsMeta, yAxisKpiDataRange);
        yInputs = yConfig ? serializeWhitespaceYAxisUserInputs(yConfig) : [];
        if (!yAxisKpiConfig) {
            yInputs = yInputs.filter((input) => !WHITESPACE_DEFAULT_Y_AXIS_EXCLUDED_INPUT_IDS.has(input.input_id));
        }
    }

    const xAxisInputs = (xInputs ?? [])
        .filter((input) => !WHITESPACE_EXCLUDED_USER_INPUT_IDS.has(input.id))
        .map((input) => ({ input_id: input.id, input_val: input.input_val }));

    return [...xAxisInputs, ...yInputs];
};

export const buildWhitespaceKpiAxisPreviewRequest = (
    gridRequest: Record<string, unknown>,
    kpiConfig: KpiAxisConfiguration,
): Record<string, unknown> => {
    const existingInputs = (gridRequest.user_inputs as WhitespaceUserInput[] | undefined) ?? [];
    const filteredInputs = existingInputs.filter(
        (entry) => !WHITESPACE_KPI_AXIS_PREVIEW_INPUT_IDS.has(entry.input_id),
    );
    const kpiEntries = serializeWhitespaceYAxisUserInputs(kpiConfig);
    const pagination = (gridRequest.pagination as Record<string, unknown> | undefined) ?? {};
    const rowPagination = (pagination.row as Record<string, unknown> | undefined) ?? {};
    const fetchLimit =
        kpiConfig.binningMethod === 'uniform'
            ? kpiConfig.binCount
            : Math.max(kpiConfig.breakpoints.length + 1, 1);

    return {
        ...gridRequest,
        user_inputs: [...filteredInputs, ...kpiEntries],
        pagination: {
            ...pagination,
            row: {
                ...rowPagination,
                limit: fetchLimit,
                offset: 0,
            },
            column: {
                ...((pagination.column as Record<string, unknown> | undefined) ?? {}),
                offset: 0,
            },
        },
    };
};

/** X-axis (column) dimension preview: draft sort/show-top settings cap `pagination.column`. */
const buildWhitespaceColumnAxisPreviewRequest = (
    gridRequest: Record<string, unknown>,
    axisConfig: AxisConfiguration,
): Record<string, unknown> => {
    const existingInputs = (gridRequest.user_inputs as WhitespaceUserInput[] | undefined) ?? [];
    const filteredInputs = existingInputs.filter((entry) => !AXIS_PREVIEW_USER_INPUT_IDS.has(entry.input_id));
    const axisSerialized =
        serializePpaChartUserInputs(
            buildDimensionAxisUserInputs({
                ...axisConfig,
                groupRestAsOther: false,
                showOwnVendorFirst: false,
            }),
        ) ?? [];
    const axisEntries = toWhitespaceUserInputs(axisSerialized).filter(
        (entry) => !WHITESPACE_EXCLUDED_USER_INPUT_IDS.has(entry.input_id),
    );
    const pagination = (gridRequest.pagination as Record<string, unknown> | undefined) ?? {};
    const columnPagination = (pagination.column as Record<string, unknown> | undefined) ?? {};
    const columnPageLimit =
        typeof columnPagination.limit === 'number' && Number.isFinite(columnPagination.limit)
            ? columnPagination.limit
            : DEFAULT_WHITESPACE_PAGE_LIMIT;
    const fetchLimit = axisConfig.showTop > 0 ? axisConfig.showTop : columnPageLimit;

    return {
        ...gridRequest,
        user_inputs: [...filteredInputs, ...axisEntries],
        pagination: {
            ...pagination,
            column: {
                ...columnPagination,
                limit: fetchLimit,
                offset: 0,
            },
            row: {
                ...((pagination.row as Record<string, unknown> | undefined) ?? {}),
                offset: 0,
            },
        },
    };
};

/**
 * Y-axis attribute-bucket (e.g. Pack_Range) dimension preview: draft sort and
 * Top N settings cap `pagination.row`, mirroring how the X-axis's own
 * `showTop` caps `pagination.column` in the column preview below. Absent
 * Top N leaves the row limit untouched - every bucket is returned (the
 * backend ignores row pagination entirely in that case).
 */
const buildWhitespaceRowAxisPreviewRequest = (
    gridRequest: Record<string, unknown>,
    axisConfig: AxisConfiguration,
): Record<string, unknown> => {
    const existingInputs = (gridRequest.user_inputs as WhitespaceUserInput[] | undefined) ?? [];
    const filteredInputs = existingInputs.filter(
        (entry) => !WHITESPACE_BUCKET_AXIS_USER_INPUT_IDS.has(entry.input_id),
    );
    const axisEntries = buildWhitespaceYAxisGroupByUserInputs(axisConfig);
    const pagination = (gridRequest.pagination as Record<string, unknown> | undefined) ?? {};
    const rowPagination = (pagination.row as Record<string, unknown> | undefined) ?? {};

    return {
        ...gridRequest,
        user_inputs: [...filteredInputs, ...axisEntries],
        pagination: {
            ...pagination,
            row: {
                ...rowPagination,
                ...(axisConfig.showTop > 0 ? { limit: axisConfig.showTop } : null),
                offset: 0,
            },
            column: {
                ...((pagination.column as Record<string, unknown> | undefined) ?? {}),
                offset: 0,
            },
        },
    };
};

export type WhitespaceAxisPreviewKind = 'column' | 'row';

/** Builds a one-shot preview request for the shared AxisConfigurationModal - `column` for X-axis dimensions, `row` for Y-axis attribute-bucket dimensions (e.g. Pack_Range). */
export const buildWhitespaceAxisPreviewRequest = (
    gridRequest: Record<string, unknown>,
    axisConfig: AxisConfiguration,
    axisKind: WhitespaceAxisPreviewKind = 'column',
): Record<string, unknown> =>
    axisKind === 'row'
        ? buildWhitespaceRowAxisPreviewRequest(gridRequest, axisConfig)
        : buildWhitespaceColumnAxisPreviewRequest(gridRequest, axisConfig);
